import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import Login from './components/Login';
import Logout from './components/Logout';
import Dashboard from './components/Dashboard';
import Future from './components/Dashboard/Future';
import Marketplace from './components/Dashboard/Marketplace';
import { isMarketplaceAvailable } from './utils/onDemandOffer';
import MySchedule from './components/Dashboard/MySchedule';
import Dispatch from './components/Dispatch/Dispatch';
import CompleteShift from './components/Dispatch/CompleteShift';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import BrandLoader from './components/BrandLoader';
import EmergencyAlarm from './components/EmergencyAlarm';
import SafetyNoticeDialog from './components/SafetyNoticeDialog';
import { SAFETY_NOTICE_VERSION } from './components/Availability/disclaimerConfig';
import comms from './components/comms';
import { useWakeLock } from './hooks/useWakeLock';
import { useDynamicBranding } from './hooks/useDynamicBranding';
import { useVersionCheck } from './hooks/useVersionCheck';
import { Box, Alert, Toolbar } from '@mui/material';

// Define libraries as static constant to avoid re-creation each render
const GOOGLE_MAPS_LIBRARIES = ['places'];

// useJsApiLoader (which useLoadScript wraps) only ever calls loader.load() once, in a
// useEffect with an empty dependency array — it does NOT re-run when googleMapsApiKey
// changes on a later render. App mounts before login, when no key is available yet, so
// calling useLoadScript directly in App would trigger that one-and-only load with an
// empty key (see the "NoApiKeys" console warning) and then never retry once the real
// per-fleet key arrives after login — window.google.maps stays keyless for the whole
// session and Places Autocomplete silently fails. Mounting the hook in this child only
// once googleApiKey is truthy, keyed by the key itself, guarantees the one load that
// actually happens uses the real key.
function GoogleMapsScriptBridge({ apiKey, onStatusChange }) {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
    id: 'google-map-script',
  });
  useEffect(() => {
    onStatusChange(isLoaded, loadError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, loadError]);
  return null;
}

function App() {
  const [socket, setSocket] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Lifted out of Sidebar so the Dashboard "Messages" tile can also open the
  // same ChatDialog instead of falling back to a generic 'dispatch' navigate
  // that can dead-end on CompleteShift when the itinerary is already done.
  const [chatOpen, setChatOpen] = useState(false);
  // Set when "Request a callback" should auto-send a message once the chat
  // dialog opens — lets the driver see the same sent-message confirmation as
  // typing it manually, instead of a silent side-channel with no visible proof.
  const [pendingChatMessage, setPendingChatMessage] = useState(null);
  const openChatWithMessage = (message) => {
    if (message) setPendingChatMessage(message);
    setChatOpen(true);
  };
  const [activeComponent, setActiveComponent] = useState('login');
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('userSettings'));
  const [isNavigating, setIsNavigating] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [brandingReady, setBrandingReady] = useState(false);
  const [initialLoadStartTime] = useState(() => Date.now());
  
  // Version check for cache busting
  const { needsUpdate } = useVersionCheck();

  // Wake Lock to keep screen active
  const { isSupported: wakeLockSupported, isActive: wakeLockActive, requestWakeLock, error: wakeLockError } = useWakeLock();
  
  // Add state for shared data that was previously in localStorage
  const [userSettings, setUserSettings] = useState(() => {
    const stored = localStorage.getItem('userSettings');
    return stored ? JSON.parse(stored) : null;
  });
  
  const [fleetSettings, setFleetSettings] = useState(() => {
    const stored = localStorage.getItem('fleetSettings');
    return stored ? JSON.parse(stored) : null;
  });
  
  // Get fleet code for dynamic branding
  const fleetCode = fleetSettings?.unitId || localStorage.getItem('fleetCode') || null;
  
  // Apply dynamic branding based on fleet code
  useDynamicBranding(fleetCode, (ready) => {
    if (ready) {
      setBrandingReady(true);
    }
  });
  
  // Handle initial page load - wait for branding and React to be ready
  useEffect(() => {
    // Check if initial load conditions are met
    const checkInitialLoadComplete = () => {
      // Fleet code is detected (either from fleetSettings or localStorage)
      const hasFleetCode = fleetCode !== null || localStorage.getItem('fleetCode') !== null;
      
      // Branding is ready
      const brandingIsReady = brandingReady || !hasFleetCode; // If no fleet code, use default (ready)
      
      // React is mounted (component has rendered)
      const reactIsReady = true; // Always true once this effect runs
      
      // Minimum display time (400ms to prevent flicker)
      const minDisplayTime = 400;
      const elapsed = Date.now() - initialLoadStartTime;
      const minTimeElapsed = elapsed >= minDisplayTime;
      
      if (brandingIsReady && reactIsReady && minTimeElapsed) {
        // Add a small delay for smooth transition
        setTimeout(() => {
          setIsInitialLoading(false);
        }, 100);
      }
    };
    
    // Check immediately and also set up interval for polling
    checkInitialLoadComplete();
    
    // Poll every 50ms to check if conditions are met
    const interval = setInterval(checkInitialLoadComplete, 50);
    
    // Also check after a delay to ensure minimum display time
    const timeout = setTimeout(() => {
      checkInitialLoadComplete();
    }, 400);
    
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [fleetCode, brandingReady, initialLoadStartTime]);
  
  const [selectedVehicle, setSelectedVehicle] = useState(() => {
    const stored = localStorage.getItem('selectedVehicle');
    return stored ? JSON.parse(stored) : null;
  });
  
  // Note: selectedRoute and routeDetails are now managed through driverAuthData only
  // No longer using localStorage for these values - they come from auth response and socket updates
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [routeDetails, setRouteDetails] = useState(null);
  
  const googleKey =
    fleetSettings?.googleApiKey ||
    fleetSettings?.googleAPIKey ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [googleApiKey, setGoogleApiKey] = useState(googleKey || null);

  useEffect(() => {
    const k =
      fleetSettings?.googleApiKey ||
      fleetSettings?.googleAPIKey ||
      process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
      null;
    if (k) setGoogleApiKey(k);
  }, [fleetSettings]);
  const [driverAuthData, setDriverAuthData] = useState(null);
  
  // Add state to track app mode (dashboard vs dispatch)
  const [appMode, setAppMode] = useState('dashboard'); // 'dashboard' or 'dispatch'

  // Add state to track current dispatch type for sidebar menu
  const [dispatchType, setDispatchType] = useState('live'); // 'live', 'itinerary', 'shuttlebus-looped', etc.

  const [showSafetyNotice, setShowSafetyNotice] = useState(false);
  // Ref so the stable handleLoginSuccess callback (empty deps) can read the latest target.
  const pendingTargetRef = useRef(null);
  
  // Request wake lock immediately when app loads to keep screen active at all times
  useEffect(() => {
    if (wakeLockSupported && !wakeLockActive) {
      requestWakeLock();
    }
  }, [wakeLockSupported, wakeLockActive, requestWakeLock]);

  // Log wake lock errors separately to avoid triggering re-requests
  useEffect(() => {
    if (wakeLockError) {
      console.warn('Wake Lock Error:', wakeLockError);
    }
  }, [wakeLockError]);
  
  // Google Maps load state — populated by GoogleMapsScriptBridge (see comment above it),
  // which isn't mounted until googleApiKey is actually known.
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(undefined);
  const handleMapsStatusChange = useCallback((loaded, err) => {
    setGoogleMapsLoaded(loaded);
    setLoadError(err);
  }, []);
  const mapsLoaderBridge = googleApiKey ? (
    <GoogleMapsScriptBridge key={googleApiKey} apiKey={googleApiKey} onStatusChange={handleMapsStatusChange} />
  ) : null;

  // Log Google Maps loading state for debugging (dev only)
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('App.js - Google Maps state:', {
        googleApiKey: googleApiKey ? 'present' : 'missing',
        googleApiKeyLength: googleApiKey?.length,
        googleMapsLoaded: googleMapsLoaded,
        loadError: loadError
      });
    }
  }, [googleApiKey, googleMapsLoaded, loadError]);

  const toggleSidebar = () => {

    setSidebarOpen(!sidebarOpen);
  };

  // Sub-screens (e.g. Availability/Scheduling) can claim the header's refresh
  // button for their own data reload instead of a full page reload — see
  // activeScreenRefresh below, registered via onRegisterRefresh.
  const [activeScreenRefresh, setActiveScreenRefresh] = useState(null);

  const handleRefreshClick = () => {
    if (activeScreenRefresh) {
      activeScreenRefresh();
      return;
    }
    window.location.href='/';
  };

  const handleLoginSuccess = useCallback((socketInstance, settings, nextComponent, authData) => {
    console.log('App: Starting login success handling with data:', { nextComponent, authData });
    
    // Show loader during login transition
    setIsNavigating(true);
    
    // Set socket first
    setSocket(socketInstance);
    console.log('App: Socket connected:', socketInstance);
    
    // Update fleet settings (this will trigger branding update via useDynamicBranding)
    setFleetSettings(settings);
    setGoogleApiKey(
      settings?.googleApiKey || process.env.REACT_APP_GOOGLE_MAPS_API_KEY || null
    );
    
    // Handle auth data (from new /Driver/Authorize endpoint)
    if (authData) {
      // Store dashboard data if available (only for dashboard mode)
      if (authData.dashboardData) {
        localStorage.setItem('dashboardData', JSON.stringify(authData.dashboardData));
      }
      
      // Process dispatch data - create merged auth data structure
      let mergedAuthData = { ...authData };
      
      if (authData.dispatchData) {
        // Merge the auth data with dispatch data to maintain full context
        mergedAuthData = {
          ...authData,
          ...authData.dispatchData, // Spread dispatch data properties to top level
          dispatchData: authData.dispatchData // But also keep the original structure
        };
        
        // CRITICAL: The spread above overwrites top-level driverObject (camelCase fields from REST)
        // with dispatchData.driverObject (PascalCase fields from formatCompleteDriverObject).
        // Preserve the top-level camelCase driverObject, since downstream code expects camelCase.
        // After server fix, both versions now have both casings, but this ensures the 
        // originally-mapped camelCase version takes priority for backward compatibility.
        if (authData.driverObject) {
          mergedAuthData.driverObject = authData.driverObject;
        }
        
        // If route data exists, set it in state (NOT localStorage)
        if (authData.dispatchData.routeObject && Object.keys(authData.dispatchData.routeObject).length > 0) {
          console.log('App: Setting initial route details from auth:', authData.dispatchData.routeObject);
          setRouteDetails(authData.dispatchData.routeObject);
        }
      }
      
      // Set the complete merged auth data
      console.log('App: Setting driver auth data:', mergedAuthData);
      setDriverAuthData(mergedAuthData);
    }
    
    // Sync userSettings from localStorage into React state.
    // comms.js stores userSettings to localStorage during auth, but without this
    // call the React state stays null until a page reload - causing downstream
    // components (DemandResponse, FlagTripDropoffModal) to receive null userSettings
    // and lose access to driverId.
    const storedUserSettings = localStorage.getItem('userSettings');
    if (storedUserSettings) {
      try {
        setUserSettings(JSON.parse(storedUserSettings));
      } catch (e) {
        console.error('App: Failed to parse userSettings from localStorage:', e);
      }
    }
    
    // Set authentication state
    setIsAuthenticated(true);
    
    // ── Dashboard landing override ───────────────────────────────────
    // The per-fleet config (admin.tranware.net) exposes `dashboard: true`
    // for fleets that should land on the home dashboard after login
    // (currently ACET). This flag is the authoritative, robust signal —
    // the config response carries no fleetCode/unitId field, so we cannot
    // rely on a fleetcode string match. Fleets without the flag keep the
    // existing defaultScreen/dispatch behavior unchanged (no regression).
    const resolvedFleetCode = (
      settings?.unitId ||
      settings?.fleetCode ||
      localStorage.getItem('fleetCode') ||
      ''
    ).toUpperCase();
    const dashboardEnabled =
      settings?.dashboard === true ||
      authData?.dashboard === true ||
      authData?.defaultScreen === 'dashboard' ||
      !!authData?.dashboardData;
    // The dashboard landing is intentionally limited to the ACET fleet. Other
    // fleets (e.g. DEVI) also ship `dashboard:true` in their server config, so
    // the flag alone is NOT a reliable ACET signal — without the fleet-code
    // gate every dashboard-flagged fleet would land on the home dashboard.
    // Gating on the ACET fleet code keeps all other fleets on their existing
    // dispatch landing behavior (no regression).
    const isAcetFleet = resolvedFleetCode === 'ACET';
    const forceDashboard = dashboardEnabled && isAcetFleet;
    console.log('App: dashboard routing decision:', {
      resolvedFleetCode,
      settingsDashboard: settings?.dashboard,
      dashboardEnabled,
      isAcetFleet,
      forceDashboard,
      nextComponent,
    });

    // Determine app mode based on ACET override, defaultScreen, or nextComponent
    const isDashboardMode =
      forceDashboard ||
      authData?.defaultScreen === 'dashboard' ||
      nextComponent === 'dashboard';
    setAppMode(isDashboardMode ? 'dashboard' : 'dispatch');

    // For ACET dashboard fleets, override the landing screen to the home dashboard.
    const targetComponent = forceDashboard ? 'dashboard' : nextComponent;

    // Use setTimeout to ensure all state updates are processed before navigation
    setTimeout(() => {
      console.log('App: Navigating to component:', targetComponent);
      // Always navigate to the target first so the app leaves the login render path.
      // For ACET fleet the safety notice dialog then appears on top of the target screen.
      if (targetComponent) setActiveComponent(targetComponent);
      if (isAcetFleet) {
        pendingTargetRef.current = targetComponent;
        setShowSafetyNotice(true);
        setIsNavigating(false);
      } else {
        setTimeout(() => setIsNavigating(false), 200);
      }
    }, 0);
  }, []); // Empty dependency array since we want a stable callback

  const handleSafetyNoticeOk = useCallback(() => {
    setShowSafetyNotice(false);
    const uid = JSON.parse(localStorage.getItem('userSettings') || '{}')?.Id || 'unknown';
    const ackKey = `safety_notice_ack_${uid}_${SAFETY_NOTICE_VERSION}`;
    localStorage.setItem(ackKey, JSON.stringify({ driverId: uid, version: SAFETY_NOTICE_VERSION, timestamp: new Date().toISOString() }));
    try {
      comms.recordConsent?.({ driverId: uid, type: 'safety_notice', version: SAFETY_NOTICE_VERSION, timestamp: new Date().toISOString() })
        ?.catch(() => {});
    } catch {}
    if (pendingTargetRef.current) setActiveComponent(pendingTargetRef.current);
    setTimeout(() => setIsNavigating(false), 200);
  }, []);

  const handleLogout = () => {
    // Don't clear state immediately - let the Logout component handle the process
    setActiveComponent('logout');
  };

  const handleLogoutComplete = () => {
    // This will be called by the Logout component after it completes its cleanup
    setIsAuthenticated(false);
    setActiveComponent('login');
    setSocket(null);
    setUserSettings(null);
    setSelectedVehicle(null);
    setSelectedRoute(null);
    setRouteDetails(null);
    setDriverAuthData(null);
    
    // Clear localStorage - only remove items that should persist across sessions
    localStorage.removeItem('userSettings');
    // Per-driver, never per-device: a stale status would show the next driver as
    // Busy/OffService with no assigned trip.
    comms.clearCurrentStatus();
    // Note: selectedVehicle, fleetSettings, driverNumber, pin, fleetCode remain for convenience
    // dispatchData and routeDetails are not stored in localStorage anymore
  };

  // Handle post-shift-completion cleanup (bypasses Logout component since shift is already closed)
  const handleShiftCompleted = useCallback(() => {
    // CompleteShift already handled: shift completion, vehicle unassignment, odometer, inspection
    // Just clean up client state and navigate to login - preserve fleetCode and fleetSettings

    // Disconnect socket gracefully
    if (socket && typeof socket.disconnect === 'function') {
      try {
        socket.disconnect();
        console.log('Socket disconnected after shift completion');
      } catch (error) {
        console.error('Error disconnecting socket:', error);
      }
    }

    // Clear user-specific localStorage but PRESERVE fleetCode and fleetSettings
    localStorage.removeItem('userSettings');
    localStorage.removeItem('selectedRouteId');
    localStorage.removeItem('selectedRoute');
    localStorage.removeItem('routeDetails');
    localStorage.removeItem('driverNumber');
    localStorage.removeItem('pin');
    // The shift is over, so the status that went with it must not outlive it.
    comms.clearCurrentStatus();

    // Reset React state
    setIsAuthenticated(false);
    setActiveComponent('login');
    setIsNavigating(false);
    setSocket(null);
    setUserSettings(null);
    setSelectedVehicle(null);
    setSelectedRoute(null);
    setRouteDetails(null);
    setDriverAuthData(null);
  }, [socket]);

  // Multi-shift day (route self-scheduling §4.9): the driver finished one
  // claimed route but holds another (non-overlapping) route later today.
  // Keep the session/socket alive; clear only the just-closed shift so
  // determineDispatchStage re-enters openShift and maps the next route.
  const handleBeginNextShift = useCallback(() => {
    localStorage.removeItem('selectedRouteId');
    localStorage.removeItem('selectedRoute');
    localStorage.removeItem('routeDetails');
    setSelectedRoute(null);
    setRouteDetails(null);
    setDriverAuthData((prev) => {
      if (!prev) return prev;
      const nextDispatchData = prev.dispatchData
        ? { ...prev.dispatchData, shiftObject: null, routeObject: null }
        : prev.dispatchData;
      return {
        ...prev,
        shiftId: null,
        shiftObject: null,
        routeObject: null,
        shiftRequired: true,
        dispatchData: nextDispatchData,
      };
    });
    setActiveComponent('dispatch');
  }, []);

  const handleComponentChange = (componentName) => {
    // Show loader if component is actually changing
    if (componentName !== activeComponent) {
      setIsNavigating(true);
    }
    setActiveComponent(componentName);
    setSidebarOpen(false);
    
    // Hide loader after a short delay (component should be mounted by then)
    setTimeout(() => {
      setIsNavigating(false);
    }, 100);
  };

  // Handler for dispatch data updates (shift opening, route selection, etc.)
  const handleDispatchDataUpdate = useCallback((updateData) => {
    console.log('App: Dispatch data updated:', updateData);
    
    // Update driver auth data if provided
    if (updateData.driverAuthData) {
      console.log('App: Updating driver auth data:', updateData.driverAuthData);
      setDriverAuthData(updateData.driverAuthData);
    }
    
    // Update route details if provided (state only, no localStorage)
    if (updateData.routeDetails) {
      console.log('App: Updating route details:', updateData.routeDetails);
      setRouteDetails(updateData.routeDetails);
    }
    
    // Update selected vehicle if provided
    if (updateData.selectedVehicle) {
      console.log('App: Updating selected vehicle:', updateData.selectedVehicle);
      setSelectedVehicle(updateData.selectedVehicle);
      localStorage.setItem('selectedVehicle', JSON.stringify(updateData.selectedVehicle));
    }
  }, []);

  // Add this function to allow child components to request navigation
  const navigateToComponent = (componentName) => {
    // Show loader if component is actually changing
    if (componentName !== activeComponent) {
      setIsNavigating(true);
    }
    
    if (componentName === 'login') {
      handleLogout();
    } else if (componentName === 'logout') {
      handleLogout();
    } else if (componentName === 'dashboard') {
      setAppMode('dashboard');
      setActiveComponent('dashboard');
    } else if (componentName === 'Dashboard/MySchedule' || componentName === 'myschedule') {
      setAppMode('dashboard');
      setActiveComponent('dashboard/myschedule');
    } else if (componentName === 'Dashboard/Future' || componentName === 'future') {
      setAppMode('dashboard');
      setActiveComponent('dashboard/future');
    } else if (componentName === 'Dashboard/Marketplace' || componentName === 'marketplace') {
      if (!isMarketplaceAvailable(dispatchType)) {
        setAppMode('dispatch');
        setActiveComponent('dispatch');
        return;
      }
      setAppMode('dashboard');
      setActiveComponent('dashboard/marketplace');
    } else if (componentName === 'Dispatch/CompleteShift' || componentName === 'completeShift') { 
      setAppMode('dispatch');
      setActiveComponent('dispatch/completeshift');
    } else {
      // All other components go through dispatch mode
      // The Dispatch component will handle internal routing
      setAppMode('dispatch');
      setActiveComponent('dispatch');
    }
    
    // Hide loader after component change
    setTimeout(() => {
      setIsNavigating(false);
    }, 100);
  };

  // Update shared data handlers
  const updateVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    if (vehicle) {
      localStorage.setItem('selectedVehicle', JSON.stringify(vehicle));
    } else {
      localStorage.removeItem('selectedVehicle');
    }
  };

  const updateRoute = (route, details) => {
    setSelectedRoute(route);
    setRouteDetails(details);
    // Route data is now managed only in React state, not localStorage
    // This data comes from auth response initially, then from socket updates
  };

  // Do NOT block the whole app on Google Maps here: useLoadScript's isMounted-gated
  // resolution can miss its window under React 18 StrictMode double-invocation, leaving
  // googleMapsLoaded/loadError both stuck at their initial values forever (permanent
  // "Loading Google Maps..." hang). Instead, exactly like LiveDispatch/WaypointMap.js,
  // let each map-bearing screen check window.google.maps readiness itself and render a
  // local loading/unavailable fallback — the rest of the app (dashboard, dispatch,
  // trip actions) keeps working regardless of Maps script status.

  // Google Maps failed to load (e.g. no network, invalid key, quota exceeded).
  // Render the app without maps rather than crashing — components guard on googleMapsLoaded.
  if (loadError) {
    console.error('Google Maps failed to load:', loadError);
    // Do NOT return here — let the app continue with googleMapsLoaded=false so map
    // components render their "maps unavailable" fallback instead of crashing.
  }

  // Show initial load loader (full screen, highest priority) - must be checked before any other rendering
  if (isInitialLoading) {
    return <BrandLoader isLoading={true} fleetCode={fleetCode} minDisplayTime={400} />;
  }

  // Component map for rendering - pass shared data as props
  const componentMap = {
    'login': <Login 
      onLoginSuccess={handleLoginSuccess}
      fleetSettings={fleetSettings}
      selectedVehicle={selectedVehicle}
      onVehicleChange={updateVehicle}
      onRouteChange={updateRoute}
    />,
    'logout': <Logout 
      onLogout={handleLogoutComplete}
      userSettings={userSettings}
      selectedVehicle={selectedVehicle}
      driverAuthData={driverAuthData}
      selectedRoute={selectedRoute}
      fleetSettings={fleetSettings}
      routeDetails={routeDetails}
      socket={socket}
    />,
    'dashboard': <Dashboard
      navigateTo={navigateToComponent}
      userSettings={userSettings}
      fleetSettings={fleetSettings}
      googleMapsLoaded={googleMapsLoaded || false}
      googleApiKey={googleApiKey}
      selectedVehicle={selectedVehicle}
      dispatchType={dispatchType}
      onOpenChat={openChatWithMessage}
      onRegisterRefresh={setActiveScreenRefresh}
    />,
    'dashboard/future': <Future
      navigateTo={navigateToComponent}
      userSettings={userSettings}
      fleetSettings={fleetSettings}
    />,
    'dashboard/myschedule': <MySchedule
      userSettings={userSettings}
      fleetSettings={fleetSettings}
      onRegisterRefresh={setActiveScreenRefresh}
    />,
    'dashboard/marketplace': (
      <div className="min-h-full bg-gray-100">
        <div className="sticky top-0 z-10 flex items-center gap-3 bg-white px-4 h-14 shadow-sm">
          <button
            onClick={() => navigateToComponent('dispatch')}
            aria-label="Back"
            className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-gray-100"
          >
            <span className="text-2xl leading-none text-gray-800">&#8592;</span>
          </button>
          <h1 className="text-lg font-bold text-gray-900">Marketplace</h1>
        </div>
        <div className="mx-auto max-w-md px-4 py-4">
          <Marketplace userSettings={userSettings} fleetSettings={fleetSettings} />
        </div>
      </div>
    ),
    'dispatch': <Dispatch 
      navigateTo={navigateToComponent}
      userSettings={userSettings}
      fleetSettings={fleetSettings}
      socket={socket}
      routeDetails={routeDetails}
      driverAuthData={driverAuthData}
      selectedVehicle={selectedVehicle}
      onDispatchDataUpdate={handleDispatchDataUpdate}
      googleMapsLoaded={googleMapsLoaded}
      onDispatchTypeChange={setDispatchType}
    />,
    'dispatch/completeshift': <CompleteShift 
      navigateTo={navigateToComponent}
      userSettings={userSettings}
      fleetSettings={fleetSettings}
      socket={socket}
      routeDetails={driverAuthData?.routeObject || driverAuthData?.dispatchData?.routeObject || routeDetails}
      driverAuthData={driverAuthData}
      vehicleDetails={selectedVehicle}
      selectedVehicle={selectedVehicle}
      googleMapsLoaded={googleMapsLoaded || false}
      inspectionData={driverAuthData?.inspectionObject || driverAuthData?.inspectionData}
      onDispatchDataUpdate={handleDispatchDataUpdate}
      onShiftComplete={(completionData) => {
        // Multi-shift day: another claimed route remains today — start its
        // shift instead of logging out (route self-scheduling §4.9).
        if (completionData?.nextShift) {
          handleBeginNextShift();
          return;
        }
        // Shift is already completed server-side - bypass Logout component, go directly to login
        // This preserves fleetCode and fleetSettings in localStorage
        handleShiftCompleted();
      }}
    />
  };

  // Render login if not authenticated (except when logging out)
  if ((!isAuthenticated || activeComponent === 'login') && activeComponent !== 'logout') {
    return (
      <>
        {mapsLoaderBridge}
        {needsUpdate && (
          <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}>
            <Alert severity="info">
              New version available. Updating in 3 seconds...
            </Alert>
          </Box>
        )}
        {componentMap['login']}
        <SafetyNoticeDialog open={showSafetyNotice} onOk={handleSafetyNoticeOk} />
      </>
    );
  }

  // Handle logout component specially - it can render even when transitioning to unauthenticated
  if (activeComponent === 'logout') {
    return (
      <>
        {mapsLoaderBridge}
        {needsUpdate && (
          <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}>
            <Alert severity="info">
              New version available. Updating in 3 seconds...
            </Alert>
          </Box>
        )}
        {componentMap['logout']}
      </>
    );
  }

  // Render authenticated app with conditional Header/Sidebar based on mode
  const isDashboardMode = appMode === 'dashboard' || activeComponent === 'dashboard' || activeComponent === 'dashboard/future' || activeComponent === 'dashboard/myschedule' || activeComponent === 'dashboard/marketplace';
  const isDispatchMode = appMode === 'dispatch' || activeComponent === 'dispatch' || activeComponent === 'dispatch/completeshift';
  
  // Get fleet code for loader branding
  const loaderFleetCode = fleetSettings?.unitId || localStorage.getItem('fleetCode') || null;
  
  if (isDashboardMode) {
    // Dashboard mode: Show Header + Sidebar + Component
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'var(--app-viewport-height)', overflow: 'hidden' }}>
        {mapsLoaderBridge}
        <SafetyNoticeDialog open={showSafetyNotice} onOk={handleSafetyNoticeOk} />
        <BrandLoader isLoading={isNavigating} fleetCode={loaderFleetCode} />
        <Header
          onMenuClick={toggleSidebar}
          onRefreshClick={handleRefreshClick}
          showMenuButton={true}
        />
        {/* Spacer reserving exactly the fixed nav's footprint (nav height + iOS
            status-bar inset). Sized from --app-header-total rather than a second
            <Toolbar>, so it can't drift out of sync with the real nav height. */}
        <Box sx={{ height: 'var(--app-header-total)', flexShrink: 0 }} aria-hidden />
        <Sidebar
          open={sidebarOpen}
          onClose={toggleSidebar}
          activeComponent={activeComponent}
          onComponentChange={handleComponentChange}
          userSettings={userSettings}
          fleetSettings={fleetSettings}
          navigateToComponent={navigateToComponent}
          dispatchType={dispatchType}
          socket={socket}
          selectedVehicle={selectedVehicle}
          routeDetails={routeDetails}
          chatOpen={chatOpen}
          onOpenChat={openChatWithMessage}
          onCloseChat={() => setChatOpen(false)}
          pendingChatMessage={pendingChatMessage}
          onPendingChatMessageSent={() => setPendingChatMessage(null)}
        />
        <Box className="app-scroll-area">
          {componentMap[activeComponent] || componentMap['dashboard']}
        </Box>
        <EmergencyAlarm showFloatingButton={false} />
      </Box>
    );
  } else if (isDispatchMode) {
    // Dispatch mode: Show Header + Sidebar + Component
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'var(--app-viewport-height)', overflow: 'hidden' }}>
        {mapsLoaderBridge}
        <SafetyNoticeDialog open={showSafetyNotice} onOk={handleSafetyNoticeOk} />
        <BrandLoader isLoading={isNavigating} fleetCode={loaderFleetCode} />
        <Header 
          onMenuClick={toggleSidebar} 
          onRefreshClick={handleRefreshClick} 
          showMenuButton={true}
        />
        {/* Spacer reserving exactly the fixed nav's footprint (nav height + iOS
            status-bar inset). Sized from --app-header-total rather than a second
            <Toolbar>, so it can't drift out of sync with the real nav height. */}
        <Box sx={{ height: 'var(--app-header-total)', flexShrink: 0 }} aria-hidden />
        <Sidebar 
          open={sidebarOpen} 
          onClose={toggleSidebar} 
          activeComponent={activeComponent}
          onComponentChange={handleComponentChange}
          userSettings={userSettings}
          fleetSettings={fleetSettings}
          navigateToComponent={navigateToComponent}
          dispatchType={dispatchType}
          socket={socket}
          selectedVehicle={selectedVehicle}
          routeDetails={routeDetails}
          chatOpen={chatOpen}
          onOpenChat={openChatWithMessage}
          onCloseChat={() => setChatOpen(false)}
          pendingChatMessage={pendingChatMessage}
          onPendingChatMessageSent={() => setPendingChatMessage(null)}
        />
        <Box className="app-scroll-area">
          {componentMap[activeComponent] || componentMap['dispatch']}
        </Box>
        <EmergencyAlarm showFloatingButton={false} />
      </Box>
    );
  } else {
    // Fallback: Show only the component (for logout, etc.)
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'var(--app-viewport-height)', overflow: 'hidden' }}>
        {mapsLoaderBridge}
        <SafetyNoticeDialog open={showSafetyNotice} onOk={handleSafetyNoticeOk} />
        <BrandLoader isLoading={isNavigating} fleetCode={loaderFleetCode} />
        <Box className="app-scroll-area">
          {componentMap[activeComponent] || componentMap['dashboard']}
        </Box>
      </Box>
    );
  }
}

export default App;